import React from 'react'
import PropTypes from 'prop-types'
import './ProgressCard.scss'

/**
 * ProgressCard - Card with circular or linear progress indicator
 *
 * @param {string} title - Card title
 * @param {number} value - Current progress value (0-100)
 * @param {number} total - Total value (for display)
 * @param {string} label - Progress label (e.g., "75% Complete")
 * @param {string} type - Progress type ('circular' | 'linear')
 * @param {string} variant - Color variant
 * @param {string} size - Size ('small' | 'medium' | 'large')
 * @param {boolean} loading - Show loading state
 */
const ProgressCard = ({
	title,
	value = 0,
	total,
	label,
	type = 'circular',
	variant = 'primary',
	size = 'medium',
	loading = false
}) => {
	const percentage = Math.min(100, Math.max(0, value))

	const getVariantColor = () => {
		const colors = {
			primary: 'var(--color-primary)',
			success: 'var(--color-success)',
			warning: 'var(--color-warning)',
			danger: 'var(--color-danger)',
			info: 'var(--color-info, #6366f1)'
		}
		return colors[variant] || colors.primary
	}

	const getSizeConfig = () => {
		const configs = {
			small: { radius: 35, stroke: 6 },
			medium: { radius: 45, stroke: 8 },
			large: { radius: 55, stroke: 10 }
		}
		return configs[size] || configs.medium
	}

	const renderCircularProgress = () => {
		const { radius, stroke } = getSizeConfig()
		const normalizedRadius = radius - stroke / 2
		const circumference = normalizedRadius * 2 * Math.PI
		const strokeDashoffset = circumference - (percentage / 100) * circumference
		const svgSize = radius * 2

		return (
			<div className="progress-card__circular">
				<svg height={svgSize} width={svgSize}>
					{/* Background circle */}
					<circle
						stroke="var(--color-gray-200)"
						fill="transparent"
						strokeWidth={stroke}
						r={normalizedRadius}
						cx={radius}
						cy={radius}
					/>
					{/* Progress circle */}
					<circle
						stroke={getVariantColor()}
						fill="transparent"
						strokeWidth={stroke}
						strokeDasharray={circumference + ' ' + circumference}
						style={{ strokeDashoffset }}
						strokeLinecap="round"
						r={normalizedRadius}
						cx={radius}
						cy={radius}
						className="progress-card__circular-progress"
					/>
				</svg>
				<div className="progress-card__circular-value">
					<span className="progress-card__percentage">{percentage}%</span>
				</div>
			</div>
		)
	}

	const renderLinearProgress = () => {
		return (
			<div className="progress-card__linear">
				<div className="progress-card__linear-header">
					<span className="progress-card__percentage">{percentage}%</span>
					{total && (
						<span className="progress-card__total">
							{value} / {total}
						</span>
					)}
				</div>
				<div className="progress-card__linear-track">
					<div
						className="progress-card__linear-fill"
						style={{
							width: `${percentage}%`,
							backgroundColor: getVariantColor()
						}}
					/>
				</div>
			</div>
		)
	}

	if (loading) {
		return (
			<div className={`progress-card progress-card--${variant} progress-card--${size}`}>
				<div className="progress-card__skeleton">
					<div className="skeleton skeleton--title"></div>
					<div className={`skeleton skeleton--progress skeleton--progress-${type}`}></div>
					<div className="skeleton skeleton--label"></div>
				</div>
			</div>
		)
	}

	return (
		<div className={`progress-card progress-card--${variant} progress-card--${size}`}>
			<h3 className="progress-card__title">{title}</h3>
			<div className="progress-card__content">
				{type === 'circular' ? renderCircularProgress() : renderLinearProgress()}
			</div>
			{label && <p className="progress-card__label">{label}</p>}
		</div>
	)
}

ProgressCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.number,
	total: PropTypes.number,
	label: PropTypes.string,
	type: PropTypes.oneOf(['circular', 'linear']),
	variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	loading: PropTypes.bool
}

export default ProgressCard
